import { APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getChaptersByUser } from "@/shared/reducers/entities/edo-chapter.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import {
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Pagination,
} from "@/components/ui";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { IPaginationBaseState, TextFormat, Translate } from "react-jhipster";
import SearchField from "../SearchField/SearchField";
import { cn } from "@/shared/util/cn";

type SelectCapitoliProps =
  | {
      selectedChapters: IEdoChapter[];
      setSelectedChapters: (chapters: IEdoChapter[]) => void;
      multiple: true;
    }
  | {
      selectedChapter: IEdoChapter;
      setSelectedChapter: (chapter: IEdoChapter) => void;
      multiple: false;
    };

export const SelectCapitoli = (props: SelectCapitoliProps) => {
  const dispatch = useAppDispatch();
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const {
    entities: edoChapterList,
    updateSuccess,
    loading,
    totalItems,
  } = useAppSelector<EntityState<IEdoChapter>>((state) => state.edoChapter);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "id",
    order: "ASC",
  });
  const [searchValue, setSearchValue] = useState<string>("");

  const { multiple } = props;

  const handleGetEdoCapitoloList = () => {
    const params = new URLSearchParams();
    if (searchValue) params.append("search", searchValue);
    dispatch(
      getChaptersByUser({
        userId: account.id,
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: params.toString(),
      })
    );
  };

  useEffect(() => {
    handleGetEdoCapitoloList();
  }, [
    searchValue,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      handleGetEdoCapitoloList();
    }
  }, [updateSuccess]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const handleOnClick = (chapter: IEdoChapter) => {
    if (multiple === true) {
      const idx = props.selectedChapters.findIndex((el) => el.id === chapter.id);
      if (idx === -1) {
        props.setSelectedChapters([...props.selectedChapters, chapter]);
      } else {
        const next = [...props.selectedChapters];
        next.splice(idx, 1);
        props.setSelectedChapters(next);
      }
    } else {
      if (!props.selectedChapter) {
        props.setSelectedChapter(chapter);
      } else if (props.selectedChapter?.id === chapter?.id) {
        props.setSelectedChapter(null);
      }
    }
  };

  const isSelected = (chapter: IEdoChapter) =>
    multiple === true
      ? props.selectedChapters.findIndex((el) => el.id === chapter.id) !== -1
      : props.selectedChapter?.id === chapter?.id;

  return (
    <Card className="surface overflow-hidden rounded-2xl">
      <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-3 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-heading font-semibold text-[var(--color-mockup-text)]">
            Capitoli
          </h2>
          <div className="min-w-0 flex-1 sm:max-w-xs">
            <SearchField
              placeholder="Cerca un capitolo"
              value={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>
        </div>
      </div>
      {loading && (
        <div className="flex justify-center py-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}
      {edoChapterList.length ? (
        <List className="divide-y">
          {edoChapterList.map((chapter) => (
            <ListItem
              key={chapter.id}
              className={cn(
                "flex cursor-pointer items-center gap-3 py-3 transition-colors hover:bg-primary/5",
                isSelected(chapter) && "bg-[var(--color-surface-secondary)]/50"
              )}
              onClick={() => handleOnClick(chapter)}
            >
              <ListItemIcon className="pt-0.5">
                <input
                  type="checkbox"
                  checked={isSelected(chapter)}
                  readOnly
                  className="h-4 w-4 rounded-sm border-[var(--color-mockup-border)] text-primary focus:ring-primary"
                />
              </ListItemIcon>
              <ListItemText
                primary={chapter.title}
                secondary={
                  <TextFormat
                    value={chapter.createdAt}
                    type="date"
                    format={APP_LOCAL_DATE_FORMAT}
                  />
                }
                className="min-w-0 truncate"
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <div className="p-4 text-center">
          <p className="text-body text-[var(--color-mockup-text-mid)]">
            <Translate contentKey="edocendoApp.edoChapter.home.notFound">
              No Edo Chapters found
            </Translate>
          </p>
        </div>
      )}
      <Pagination
        count={totalItems}
        page={paginationState.activePage - 1}
        rowsPerPage={paginationState.itemsPerPage}
        onPageChange={(p) => handlePagination(p + 1)}
        onRowsPerPageChange={handleRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
        }
      />
    </Card>
  );
};
